export interface TemporaryContentModel {
  title: string;
  content: string;
  previewText?: string;
  thumbnailUrl?: string;
  thumbnailFileName?: string;
  contentId: string | null;
}

/**
 * @description 임시 롤백된 데이터를 가져옵니다.
 */
export const getTemporaryContent = () => {
  const temporaryContent = sessionStorage.getItem('temporaryContent');

  if (!temporaryContent) return null;

  return JSON.parse(temporaryContent) as TemporaryContentModel;
};

export const setTemporaryContent = (
  temporaryContent: TemporaryContentModel
) => {
  sessionStorage.setItem('temporaryContent', JSON.stringify(temporaryContent));
};

/**
 * @description 임시 롤백된 데이터를 제거합니다.
 */
export const removeTemporaryContent = () => {
  sessionStorage.removeItem('temporaryContent');
};

/**
 * @description 빈 공백만 있는 문단(`<p> </p>`, `<p></p>`, `<p>&nbsp;</p>`)이
 * 렌더러(특히 RN RenderHtml)에서 lineHeight를 만들지 못하는 케이스가 있어,
 * 결과 HTML에 `<br />`을 포함하도록 정규화합니다.
 *
 * - `p` 외의 태그에는 영향을 주지 않습니다.
 * - `p` 내부에 다른 태그가 있으면 매칭되지 않습니다.
 */
export const normalizeEmptyParagraphs = (html: string) => {
  return html.replace(
    /<p(\s[^>]*)?>(?:\s|&nbsp;)*<\/p>/g,
    (_match, attrs = '') => `<p${attrs}><br /></p>`
  );
};

/**
 * @description HTML 문자열이 실질적으로 비어있는지 확인합니다.
 * Tiptap 에디터의 경우 비어있어도 '<p></p>' 등을 반환할 수 있습니다.
 */
export const isEmpty = (html: string) => {
  if (!html) return true;

  // 태그를 제거하고 남은 텍스트가 있는지 확인
  const plainText = html.replace(/<[^>]*>/g, '').trim();

  // 텍스트는 없지만 이미지나 유튜브 영상 등이 포함되어 있는지 확인
  const hasMedia =
    html.includes('<img') ||
    html.includes('<iframe') ||
    html.includes('<video') ||
    html.includes('data-instagram-preview');

  return plainText.length === 0 && !hasMedia;
};

/**
 * @description HTML 문자열이 동일한지 확인합니다.
 */
export const isEqual = (a: string, b: string) => {
  const normalizedA = normalizeEmptyParagraphs(a);
  const normalizedB = normalizeEmptyParagraphs(b);

  if (normalizedA === normalizedB) return true;

  try {
    const parser = new DOMParser();
    const docA = parser.parseFromString(normalizedA, 'text/html');
    const docB = parser.parseFromString(normalizedB, 'text/html');

    /**
     * DOMParser를 통해 파싱된 innerHTML은 브라우저에 의해 다음과 같이 정규화됩니다:
     * 1. <img />와 <img>를 동일하게 처리 (보통 <img>로 정규화)
     * 2. 속성의 순서 정렬
     * 3. 잘못된 태그 구조 교정
     */
    return docA.body.innerHTML === docB.body.innerHTML;
  } catch (e) {
    // DOMParser를 사용할 수 없는 환경이거나 에러 발생 시,
    // 최소한의 정규화(자가 종료 태그 등) 후 비교
    const normalize = (html: string) =>
      html
        .replace(/\s\/>/g, '>')
        .replace(/\/>/g, '>')
        .replace(/\s+/g, ' ')
        .trim();

    return normalize(a) === normalize(b);
  }
};
